import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Plus, Fuel } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/format';

const empty = { vehicle_id: '', driver_name: '', date: '', fuel_type_id: '', fuel_type_name: '', fuel_station_id: '', fuel_station_name: '', liters: '', price_per_liter: '', km: '', notes: '' };

export default function FuelRecords() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [stations, setStations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fuel-records', { params: { page, limit: 10 } });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    (async () => {
      const [v, ft, fs] = await Promise.all([
        api.get('/vehicles', { params: { limit: 200 } }),
        api.get('/masters/fuel-types', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/fuel-stations', { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setVehicles(v.data.items || []); setFuelTypes(ft.data.items || []); setStations(fs.data.items || []);
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.liters) { toast.error('Veículo e litros obrigatórios'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        liters: Number(form.liters),
        price_per_liter: form.price_per_liter ? Number(form.price_per_liter) : null,
        km: form.km ? Number(form.km) : null,
        date: form.date ? new Date(form.date).toISOString() : null,
      };
      await api.post('/fuel-records', payload);
      toast.success('Abastecimento registrado'); setModalOpen(false); setForm(empty); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => { try { await api.delete(`/fuel-records/${deleteId}`); toast.success('Excluído'); setDeleteId(null); load(); } catch { toast.error('Erro'); } };

  return (
    <div>
      <PageHeader title="Abastecimentos" subtitle="Consumo médio, km/L, custo por km calculados automaticamente" onAction={() => { setForm(empty); setModalOpen(true); }} actionLabel="Novo abastecimento" />
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Veículo</th>
              <th className="text-left px-4 py-3">Motorista</th>
              <th className="text-left px-4 py-3">Posto/Combustível</th>
              <th className="text-right px-4 py-3">Litros</th>
              <th className="text-right px-4 py-3">R$/L</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">km/L</th>
              <th className="text-right px-4 py-3">R$/km</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="fuel-table" className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={10} className="p-4"><TableSkeleton rows={5} cols={10} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={10}><EmptyState icon={Fuel} title="Nenhum abastecimento" /></td></tr> :
              items.map(f => (
                <tr key={f.id} className="tr-hover">
                  <td className="px-4 py-3 text-xs">{new Date(f.date).toLocaleDateString('pt-BR')}</td>
                  <td className="px-4 py-3 font-mono text-xs">{f.vehicle_plate}</td>
                  <td className="px-4 py-3 text-xs">{f.driver_name || '-'}</td>
                  <td className="px-4 py-3 text-xs">{f.fuel_station_name || '-'} <span className="text-neutral-400">/ {f.fuel_type_name || '-'}</span></td>
                  <td className="px-4 py-3 text-right">{f.liters}</td>
                  <td className="px-4 py-3 text-right">{f.price_per_liter ? formatCurrency(f.price_per_liter) : '-'}</td>
                  <td className="px-4 py-3 text-right font-medium">{f.total_value ? formatCurrency(f.total_value) : '-'}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{f.km_per_liter || '-'}</td>
                  <td className="px-4 py-3 text-right text-xs">{f.cost_per_km ? formatCurrency(f.cost_per_km) : '-'}</td>
                  <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(f.id)}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Novo abastecimento</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Veículo *</Label>
              <Select value={form.vehicle_id} onValueChange={v => setForm(f => ({ ...f, vehicle_id: v }))}>
                <SelectTrigger data-testid="fuel-vehicle-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate} - {v.brand} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data</Label><Input type="datetime-local" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Motorista</Label><Input value={form.driver_name} onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))} /></div>
            <div className="space-y-1.5">
              <Label>Posto</Label>
              <Select value={form.fuel_station_id || 'none'} onValueChange={v => {
                if (v === 'none') { setForm(f => ({ ...f, fuel_station_id: '', fuel_station_name: '' })); return; }
                const s = stations.find(x => x.id === v);
                setForm(f => ({ ...f, fuel_station_id: v, fuel_station_name: s?.name || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {stations.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Combustível</Label>
              <Select value={form.fuel_type_id || 'none'} onValueChange={v => {
                if (v === 'none') { setForm(f => ({ ...f, fuel_type_id: '', fuel_type_name: '' })); return; }
                const t = fuelTypes.find(x => x.id === v);
                setForm(f => ({ ...f, fuel_type_id: v, fuel_type_name: t?.name || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {fuelTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Litros *</Label><Input required type="number" step="0.01" value={form.liters} onChange={e => setForm(f => ({ ...f, liters: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>R$ por litro</Label><Input type="number" step="0.001" value={form.price_per_liter} onChange={e => setForm(f => ({ ...f, price_per_liter: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Quilometragem</Label><Input type="number" value={form.km} onChange={e => setForm(f => ({ ...f, km: e.target.value }))} /></div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button data-testid="save-fuel" type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir abastecimento?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
